import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { updateProfile } from '@/api/endpoints/auth';
import { ApiError, errorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { titleCase } from '@/lib/format';
import { NotificationOptIn } from './NotificationOptIn';
import { PrivacyCard } from './PrivacyCard';
import type { UserUpdatePayload } from '@/types/api';

export function SettingsPage() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: user?.phone ?? '',
    emergency_contact_name: user?.emergency_contact_name ?? '',
    emergency_contact_phone: user?.emergency_contact_phone ?? '',
  });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const profile = useMutation({
    mutationFn: (payload: UserUpdatePayload) => updateProfile(payload),
    onSuccess: (updated) => {
      setUser(updated);
      toast.success('Profile updated.');
    },
    onError: (error) => {
      if (error instanceof ApiError && error.issues.length > 0) {
        setFieldErrors(Object.fromEntries(error.issues.map((issue) => [issue.field, issue.message])));
      }
      toast.error(errorMessage(error));
    },
  });

  const passwordChange = useMutation({
    mutationFn: (next: string) => updateProfile({ password: next }),
    onSuccess: () => {
      setPassword('');
      setConfirm('');
      toast.success('Password changed.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  function handleProfileSubmit(event: FormEvent) {
    event.preventDefault();
    setFieldErrors({});

    // Send only what changed; the backend patches on exclude_unset.
    const payload: UserUpdatePayload = {};
    if (form.name !== user?.name) payload.name = form.name;
    if (form.email !== user?.email) payload.email = form.email;
    if (form.phone !== (user?.phone ?? '')) payload.phone = form.phone;

    if (Object.keys(payload).length === 0) {
      toast.info('Nothing to save.');
      return;
    }
    profile.mutate(payload);
  }

  function handleContactSubmit(event: FormEvent) {
    event.preventDefault();
    setFieldErrors({});

    const payload: UserUpdatePayload = {};
    if (form.emergency_contact_name !== (user?.emergency_contact_name ?? ''))
      payload.emergency_contact_name = form.emergency_contact_name;
    if (form.emergency_contact_phone !== (user?.emergency_contact_phone ?? ''))
      payload.emergency_contact_phone = form.emergency_contact_phone;

    if (Object.keys(payload).length === 0) {
      toast.info('Nothing to save.');
      return;
    }
    profile.mutate(payload);
  }

  function handlePasswordSubmit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      toast.error('The two passwords do not match.');
      return;
    }
    if (password.length < 8) {
      toast.error('Use at least 8 characters.');
      return;
    }
    passwordChange.mutate(password);
  }

  return (
    <>
      <PageHeader title="Settings" description="Your profile and notification preferences." />

      <div className="grid max-w-2xl gap-4">
        <Card>
          <CardHeader
            title="Profile"
            description={user ? `Signed in as ${titleCase(user.role)}` : undefined}
          />
          <CardBody>
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <TextField
                label="Full name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                error={fieldErrors.name}
                autoComplete="name"
              />
              <TextField
                label="Email"
                type="email"
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                error={fieldErrors.email}
                autoComplete="email"
              />
              <TextField
                label="Mobile number"
                type="tel"
                value={form.phone}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
                error={fieldErrors.phone}
                autoComplete="tel"
                hint="Emergency SMS and voice calls go to this number after a crash. Keep it current."
              />
              <Button type="submit" variant="primary" loading={profile.isPending}>
                Save changes
              </Button>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Emergency contact"
            description="Next-of-kin. Texted and called alongside you after a serious crash."
          />
          <CardBody>
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <TextField
                label="Contact name"
                value={form.emergency_contact_name}
                onChange={(event) =>
                  setForm({ ...form, emergency_contact_name: event.target.value })
                }
                error={fieldErrors.emergency_contact_name}
                autoComplete="off"
              />
              <TextField
                label="Contact mobile number"
                type="tel"
                value={form.emergency_contact_phone}
                onChange={(event) =>
                  setForm({ ...form, emergency_contact_phone: event.target.value })
                }
                error={fieldErrors.emergency_contact_phone}
                autoComplete="off"
                hint="Leave blank if you have no one to nominate. You are always notified regardless."
              />
              <Button type="submit" variant="primary" loading={profile.isPending}>
                Save contact
              </Button>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Notifications"
            description="Browser alerts are extra. SMS and calls are always sent."
          />
          <CardBody>
            <NotificationOptIn />
          </CardBody>
        </Card>

        <PrivacyCard />

        <Card>
          <CardHeader title="Password" />
          <CardBody>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <TextField
                label="New password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
              <TextField
                label="Confirm new password"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required
              />
              <Button type="submit" loading={passwordChange.isPending}>
                Change password
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </>
  );
}
